import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { classNames } from '@/lib/utils';

interface FormFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  hint?: ReactNode;
  error?: string | null;
}

export const FormField = forwardRef<HTMLInputElement, FormFieldProps>(function FormField(
  { label, hint, error, className, ...inputProps },
  ref,
) {
  const generatedId = useId();
  const inputId = inputProps.name ? `field-${inputProps.name}` : generatedId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="mb-4">
      <label
        htmlFor={inputId}
        className="block text-xs font-display uppercase tracking-[0.15em] text-wood-mid mb-1.5"
      >
        {label}
      </label>
      <input
        {...inputProps}
        ref={ref}
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={classNames(
          'w-full rounded-md border-2 bg-parchment-light/80 px-3 py-2.5 text-ink shadow-inner',
          'placeholder:text-ink-light/60',
          'focus:outline-none focus:ring-2 focus:ring-candle focus:border-candle',
          'transition-colors',
          error ? 'border-blood-bright' : 'border-wood-mid/35',
          'text-base sm:text-sm',
          className,
        )}
      />
      {hint && !error && (
        <p id={hintId} className="mt-1 text-xs text-ink-light">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="mt-1 text-xs text-blood-bright">
          {error}
        </p>
      )}
    </div>
  );
});
